import { FormHandles, Scope } from '@unform/core';
import { Form } from '@unform/web';
import {
  Alert,
  HiddenInput,
  Input,
  Modal,
  Select,
  ToggleInput,
} from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { useCarriers, useValidation } from 'hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchCarrierMemberActions as FetchActions,
  UpdateCarrierMemberActions as UpdateActions,
} from 'store/ducks/carrierMembers';
import { DeleteCarrierMemberActions as DeleteActions } from 'store/ducks/carrierMembers';
import { ListCarriersActions } from 'store/ducks/carriers';
import { UpdateMemberValidator } from 'validators/CarrierMembers';
import * as S from './styles';

interface Props {
  memberId: string | number;
  onUpdate?: () => void;
}

export const MemberUpdateForm: React.FC<Props> = ({ memberId, onUpdate }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();
  const { carrierOptions, loadingCarriers, fetchCarriers } = useCarriers();

  const { data: member } = useSelector(
    (state: RootState) => state.fetchCarrierMember
  );

  const { loading: updatingMember, validationErrors } = useSelector(
    (state: RootState) => state.updateCarrierMember
  );

  const { loading: deletingMember } = useSelector(
    (state: RootState) => state.deleteCarrierMember
  );

  const fetchmember = useCallback((): void => {
    dispatch(FetchActions.request(memberId));
  }, [dispatch, memberId]);

  const onMemberLoad = useCallback((): void => {
    if (!member) return;

    const { carrier, user, userId } = member;

    const carrierOption = carrierOptions.find((o) => o.value === carrier.id);

    if (carrierOption) {
      formRef.current?.setFieldValue('carrierId', carrierOption);
    }

    formRef.current?.setFieldValue('userId', userId);
    formRef.current?.setFieldValue('user.name', user.name);
    formRef.current?.setFieldValue('user.email', user.email);
    formRef.current?.setFieldValue(
      'user.blockedAt',
      user.blockedAt !== null ? '1' : '0'
    );
  }, [member, carrierOptions]);

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef?.current?.setErrors({});

        const { schema } = new UpdateMemberValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateActions.request(memberId, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, memberId, onSuccess]
  );

  const onDelete = useCallback(() => {
    setModalOpen(false);
    dispatch(DeleteActions.request(memberId, onSuccess));
  }, [dispatch, handleFormErrors, memberId]);

  useEffect(() => {
    fetchCarriers();
  }, [fetchCarriers]);

  useEffect(() => {
    fetchmember();
  }, [fetchmember]);

  useEffect(() => {
    onMemberLoad();
  }, [onMemberLoad]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
      dispatch(ListCarriersActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <HiddenInput name="userId" />
        <Select
          name="carrierId"
          label="Transportadora"
          options={carrierOptions}
          isLoading={loadingCarriers}
        />
        <Scope path="user">
          <S.FormRow>
            <Input name="name" label="Nome completo" />
            <Input name="email" label="Email" />
            <Input name="password" label="Senha" type="password" />
            <ToggleInput name="blockedAt" label="Bloqueado" />
          </S.FormRow>
        </Scope>
        <Modal isOpen={modalOpen} onClickOutside={() => setModalOpen(false)}>
          <Alert
            title={'Excluir Usuário'}
            text={'Deseja realmente excluir este usuário?'}
            close={() => setModalOpen(false)}
            action={onDelete}
            labelAction={'Sim'}
          />
        </Modal>
        <S.FormActions>
          <S.LinkButton
            mood="light"
            to="/configuracoes/transportadoras/usuarios"
          >
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit" disabled={updatingMember}>
            {updatingMember ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
          <S.Button
            type="button"
            mood="danger"
            onClick={() => setModalOpen(true)}
            disabled={deletingMember}
          >
            {deletingMember ? <S.ActivityIndicator /> : 'Excluir'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
